import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link, RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap';
import { useState } from 'react';
import { IconCode, IconMoodSmile } from '@tabler/icons-react';
import { Group, Input, Overlay, Popover, Text, useComputedColorScheme } from '@mantine/core';

import classes from './RichTextEditorComponent.module.css';

import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { Extension } from '@tiptap/core';
import { useTranslation } from "react-i18next";

// Print-like line breaks: Enter inserts a <br> where the user wants the line to break,
// instead of starting a paragraph the ad templates cannot use. Higher priority than the
// default Enter binding so it wins over splitBlock.
const EnterAsLineBreak = Extension.create({
    name: 'enterAsLineBreak',
    priority: 1000,
    addKeyboardShortcuts() {
        return {
            Enter: () => this.editor.commands.setHardBreak(),
        };
    },
});

// Keep the manual breaks, drop the block markup the ad templates cannot use.
const serializeInlineHtml = (html) => html
    .replace(/<\/p>\s*<p[^>]*>/gi, '<br>')
    .replace(/<\/?p[^>]*>/gi, '')
    .replace(/(?:\s|&nbsp;|<br\s*\/?>)*(?:<br\s*\/?>)\s*$/i, '');

export const TipTapEditorV3 = ({ charCountProps, hasLink = true, hasCodeView = true, ...props }) => {

    const computedColorScheme = useComputedColorScheme('light');
    const [isSourceCodeModeActive, onSourceCodeTextSwitch] = useState(false);

    const toolbarBgColor = computedColorScheme === 'light' ? 'gray.3' : 'dark.8';
    const contentBgColor = computedColorScheme === 'light' ? 'gray.0' : 'dark.4';

    const editor = useEditor({
        extensions: [
            StarterKit,
            EnterAsLineBreak,
            Link,
            Placeholder.configure({ placeholder: props?.placeholder || '' }),
            CharacterCount.configure({ mode: 'textSize' }),
        ],
        shouldRerenderOnTransaction: true,
        content: props?.defaultValue,
        onUpdate: ({ editor }) => {
            props?.onChange(serializeInlineHtml(editor.getHTML()));
        },
        onBlur: (e) => props?.onBlur(e),
    });

    const charColorRanges = charCountProps?.range || [
        { color: "red", ranges: [[0, 5], [30, Infinity]] },
        { color: "orange", ranges: [[6, 14], [25, 30]] },
        { color: "green", ranges: [[15, 25]] },
    ];

    const getCharColor = (count) =>
        charColorRanges.find(({ ranges }) =>
            ranges.some(([min, max]) => count >= min && count <= max)
        )?.color || null;

    const RTEControlsGroup = ({ children }) => (
        <RichTextEditor.ControlsGroup pos="relative" bg={toolbarBgColor}>
            {isSourceCodeModeActive && (<Overlay color="#000" blur={15} radius="md" />)}
            {children}
        </RichTextEditor.ControlsGroup>);

    return (
        <Input.Wrapper className="wrapper-label" label={props?.label || ''} description={props?.description || ''} error={props?.error || ''}>
            <RichTextEditor editor={editor} classNames={classes} onSourceCodeTextSwitch={onSourceCodeTextSwitch}>
                <RichTextEditor.Toolbar bg={toolbarBgColor} style={{ justifyContent: "space-between" }} >
                    <Group gap="md">
                        <RTEControlsGroup>
                            <RichTextEditor.Bold />
                            <RichTextEditor.Italic />
                            <RichTextEditor.Underline />
                            <RichTextEditor.Strikethrough />
                        </RTEControlsGroup>

                        {hasLink && (
                            <RTEControlsGroup>
                                <RichTextEditor.Link />
                                <RichTextEditor.Unlink />
                            </RTEControlsGroup>
                        )}

                        <RTEControlsGroup>
                            <InsertEmojiControl />
                        </RTEControlsGroup>

                        <RTEControlsGroup>
                            <RichTextEditor.Undo />
                            <RichTextEditor.Redo />
                        </RTEControlsGroup>
                        {hasCodeView && (
                            <RichTextEditor.ControlsGroup bg={toolbarBgColor}>
                                <RichTextEditor.SourceCode icon={IconCode} />
                            </RichTextEditor.ControlsGroup>
                        )}
                    </Group>

                    <RTEControlsGroup>
                        {charCountProps?.active && (
                            <Text size="sm" fw="bold" c={getCharColor(editor.storage.characterCount.characters())}>
                            {editor.storage.characterCount.characters()} / {charCountProps?.maxChar ?? 35}
                            </Text>
                        )}
                    </RTEControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content mih={80} bg={contentBgColor} {...props?.contentProps} />
            </RichTextEditor>
        </Input.Wrapper>
    );
}


const InsertEmojiControl = () => {
    const { t } = useTranslation();
    const { editor } = useRichTextEditorContext();

    return (
        <Popover width={350} position="bottom" withArrow shadow="md">
            <Popover.Target>
                <RichTextEditor.Control aria-label={t('editor.selectEmoji')} title={t('editor.selectEmoji')}>
                    <IconMoodSmile stroke={1.5} size={16} />
                </RichTextEditor.Control>
            </Popover.Target>
            <Popover.Dropdown p={0}>
                <Picker data={data} onEmojiSelect={({ native }) => editor?.commands.insertContent(native)} />
            </Popover.Dropdown>
        </Popover>
    )
}